import {
    ComponentPropsWithoutRef,
    ForwardedRef,
    ReactNode,
    forwardRef,
    useContext,
    useMemo,
} from 'react';
import { flexRender } from '@tanstack/react-table';
import {
    ITableCellContext,
    ITableRowContext,
    TableCellContext,
    TableContext,
    TableRowContext,
} from 'components/table/TableContext';
import { TableData } from 'components/table/TableData';
import { TableRow } from 'components/table/TableRow';

interface TableBodyProps extends Omit<ComponentPropsWithoutRef<'tbody'>, 'children'> {}

const TableBody = forwardRef(
    (props: TableBodyProps, ref: ForwardedRef<HTMLTableSectionElement>) => {
        const { table } = useContext(TableContext);
        const { className = '', ...rest } = props;
        const renderRow = table.options.meta?.renderRow;

        return (
            <tbody ref={ref} className={`drop-shadow-sm ${className}`} {...rest}>
                {table.getRowModel().rows.map(row => {
                    const tableRowChildren = row.getVisibleCells().map(cell => (
                        <OptimizedCellProvider key={cell.id} cell={cell}>
                            <TableData onClick={e => table.options.meta?.onRowClick?.(row, e)}>
                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                            </TableData>
                        </OptimizedCellProvider>
                    ));

                    const NextRow = (
                        <>
                            <TableRow>{tableRowChildren}</TableRow>
                            {row.getIsExpanded() && table.options.meta?.renderExpandedRow?.(row)}
                        </>
                    );

                    return (
                        <OptimizedRowProvider key={row.id} row={row}>
                            {renderRow ? renderRow(table, row, NextRow, tableRowChildren) : NextRow}
                        </OptimizedRowProvider>
                    );
                })}
            </tbody>
        );
    },
);

const OptimizedRowProvider = <T,>({
    row,
    children,
}: ITableRowContext<T> & { children: ReactNode }) => {
    const providerProps = useMemo(() => ({ row }), [row]);

    return <TableRowContext.Provider value={providerProps}>{children}</TableRowContext.Provider>;
};

const OptimizedCellProvider = <T, K>({
    cell,
    children,
}: ITableCellContext<T, K> & { children: ReactNode }) => {
    const providerProps = useMemo(() => ({ cell }), [cell]);

    return <TableCellContext.Provider value={providerProps}>{children}</TableCellContext.Provider>;
};

export { TableBody };
export type { TableBodyProps };
